import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Bell, AlertTriangle, Pause, TrendingDown, Info, CheckCheck, Trash2 } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { useNotificationStore } from '@/stores/notification-store';
import type { NotificationType } from '@/stores/notification-store';
import { cn } from '@/lib/utils';

function getTypeIcon(type: NotificationType): React.ReactElement {
  switch (type) {
    case 'workflow_failed':
      return <AlertTriangle className="h-4 w-4 text-error shrink-0" />;
    case 'approval_pending':
      return <Pause className="h-4 w-4 text-warning shrink-0" />;
    case 'convergence_dropped':
      return <TrendingDown className="h-4 w-4 text-warning shrink-0" />;
    case 'info':
      return <Info className="h-4 w-4 text-primary shrink-0" />;
  }
}

function formatTimeAgo(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${String(minutes)}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${String(hours)}h ago`;
  const days = Math.floor(hours / 24);
  return `${String(days)}d ago`;
}

export function NotificationBell(): React.ReactElement {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const notifications = useNotificationStore(s => s.notifications);
  const unreadCount = useNotificationStore(s => s.unreadCount);
  const markRead = useNotificationStore(s => s.markRead);
  const markAllRead = useNotificationStore(s => s.markAllRead);
  const clearAll = useNotificationStore(s => s.clearAll);

  const handleNotificationClick = (id: string, link?: string): void => {
    markRead(id);
    if (link) {
      setOpen(false);
      navigate(link);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon-sm" className="relative" aria-label="Notifications">
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-error px-1 text-[10px] font-bold text-white">
              {unreadCount > 99 ? '99+' : String(unreadCount)}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-80 bg-surface-elevated border-border p-0"
      >
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <span className="text-sm font-medium text-text-primary">Notifications</span>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="xs"
              onClick={markAllRead}
              className="text-text-secondary hover:text-text-primary"
            >
              <CheckCheck className="h-3 w-3" />
              Mark all read
            </Button>
          )}
        </div>

        <div className="max-h-80 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="flex items-center justify-center py-8 text-sm text-text-tertiary">
              No notifications
            </div>
          ) : (
            notifications.map(notif => (
              <button
                key={notif.id}
                type="button"
                onClick={(): void => {
                  handleNotificationClick(notif.id, notif.link);
                }}
                className={cn(
                  'flex w-full items-start gap-3 px-3 py-2.5 text-left transition-colors hover:bg-surface',
                  !notif.read && 'bg-primary/5'
                )}
              >
                {getTypeIcon(notif.type)}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        'text-sm truncate',
                        notif.read ? 'text-text-secondary' : 'font-medium text-text-primary'
                      )}
                    >
                      {notif.title}
                    </span>
                    {!notif.read && (
                      <span className="flex h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                    )}
                  </div>
                  <p className="text-xs text-text-tertiary truncate mt-0.5">{notif.message}</p>
                  <span className="text-[10px] text-text-tertiary mt-0.5">
                    {formatTimeAgo(notif.timestamp)}
                  </span>
                </div>
              </button>
            ))
          )}
        </div>

        {notifications.length > 0 && (
          <div className="border-t border-border px-3 py-2">
            <Button
              variant="ghost"
              size="xs"
              onClick={clearAll}
              className="w-full text-text-secondary hover:text-text-primary"
            >
              <Trash2 className="h-3 w-3" />
              Clear all
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
